import React, { useEffect, useState, useCallback, SetStateAction } from 'react';
import debounce from 'lodash.debounce';
import { SavingsForm } from './SavingsForm';
import { SavingsChart } from './SavingsChart';
import { IFinancialCalculatorInputModuleFields } from '@/types/contentful';
import { trackWithFingerprint } from '~/utils/trackWithFingerprint';
import RichText from '../RichText';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Block, Inline } from '@contentful/rich-text-types';
import { usePublicRuntimeConfig } from '~/hooks/usePublicRuntimeConfig';
import { ArrowIcon } from '../LearningCenter/Components/ArticleSnippet';

const RICH_TEXT_OVERRIDE: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
    return <p className="typography-body-2 font-light">{children}</p>;
  }
};

interface CompoundInterestComponents {
  principal: number;
  annualRate: number;
  t: number;
  additionalContribution: number;
  contributionPeriods: number;
}

export const SavingsCalculator = (
  fields: IFinancialCalculatorInputModuleFields
) => {
  const { moduleHeader, moduleSubheader } = fields;
  const [formManipulated, setFormManipulated] = useState(false);
  const [currentSavings, setCurrentSavings] = useState('100');
  const [rateOfReturn, setRateOfReturn] = useState<number | 'custom'>(0.06);
  const [customRate, setCustomRate] = useState<string>('0');
  const [extraContribution, setExtraContribution] = useState('10');
  const [contributionFrequency, setContributionFrequency] = useState(12);
  const [estimatedSavings, setEstimatedSavings] = useState(0);
  const [chartData, setChartData] = useState([] as any);
  const [savingsAgeRange, setSavingsAgeRange] = useState<[number, number]>([
    8, 16
  ]);

  const { webregUrl } = usePublicRuntimeConfig();

  /**
   * Debounced function for tracking form changes.
   * This ensures that tracking is only performed after 500ms of inactivity.
   */
  const debouncedTrackFormChanges = useCallback(
    debounce((data: any) => {
      trackWithFingerprint('Form Manipulated', data);
    }, 500),
    []
  );

  const formManipulate = useCallback(
    <T,>(
      field: keyof typeof formData,
      setter: React.Dispatch<React.SetStateAction<T>>,
      value: React.SetStateAction<T>
    ) => {
      setter(value);
      if (!formManipulated) {
        setFormManipulated(true);
      }
    },
    [formManipulated]
  );

  useEffect(() => {
    if (!formManipulated) return;

    const latestFormData = {
      form_type: 'Savings Calculator',
      current_savings: String(currentSavings),
      rate_of_return:
        rateOfReturn === 'custom' ? String(customRate) : rateOfReturn,
      extra_contribution: String(extraContribution),
      contribution_frequency: Number(contributionFrequency),
      savings_age_range: savingsAgeRange
    };

    debouncedTrackFormChanges(latestFormData);
  }, [
    formManipulated,
    currentSavings,
    rateOfReturn,
    customRate,
    extraContribution,
    contributionFrequency,
    savingsAgeRange
  ]);

  /**
   * Define state handlers that utilize formManipulate.
   */
  const stateHandlers = {
    setCurrentSavings: (value: SetStateAction<string>) =>
      formManipulate('currentSavings', setCurrentSavings, value),
    setRateOfReturn: (value: SetStateAction<number | 'custom'>) =>
      formManipulate('rateOfReturn', setRateOfReturn, value),
    setCustomRate: (value: SetStateAction<string>) =>
      formManipulate('customRate', setCustomRate, value),
    setExtraContribution: (value: SetStateAction<string>) =>
      formManipulate('extraContribution', setExtraContribution, value),
    setContributionFrequency: (value: SetStateAction<number>) =>
      formManipulate('contributionFrequency', setContributionFrequency, value),
    setSavingsAgeRange: (value: SetStateAction<[number, number]>) =>
      formManipulate('savingsAgeRange', setSavingsAgeRange, value)
  };

  /**
   * Aggregated form data to pass to child components.
   */
  const formData = {
    currentSavings,
    rateOfReturn,
    customRate,
    extraContribution,
    contributionFrequency,
    savingsAgeRange,
    estimatedSavings
  };

  /**
   * Effect to perform initial calculation on component mount.
   */
  useEffect(() => {
    const initialComponents: CompoundInterestComponents = {
      principal: parseFloat(currentSavings),
      annualRate:
        rateOfReturn === 'custom'
          ? isNaN(parseFloat(customRate))
            ? 0
            : parseFloat(customRate) / 100
          : rateOfReturn,
      t: savingsAgeRange[1] - savingsAgeRange[0],
      additionalContribution: parseFloat(extraContribution),
      contributionPeriods: contributionFrequency
    };
    handleCalculate(initialComponents);
    generateChartData(initialComponents);
  }, []);

  /**
   * Effect to perform calculation whenever relevant state changes.
   */
  useEffect(() => {
    const components: CompoundInterestComponents = {
      principal: parseFloat(currentSavings),
      annualRate:
        rateOfReturn === 'custom'
          ? isNaN(parseFloat(customRate))
            ? 0
            : parseFloat(customRate) / 100
          : rateOfReturn,
      t: savingsAgeRange[1] - savingsAgeRange[0],
      additionalContribution: parseFloat(extraContribution),
      contributionPeriods: contributionFrequency
    };
    handleCalculate(components);
    generateChartData(components);
  }, [
    currentSavings,
    savingsAgeRange,
    rateOfReturn,
    extraContribution,
    contributionFrequency,
    customRate
  ]);

  /**
   * Function to calculate total savings and contributions.
   * @param components - The input components for calculation.
   * @returns An object containing total and totalContributions.
   */
  const calculateTotal = (components: CompoundInterestComponents) => {
    const {
      principal,
      annualRate,
      t,
      additionalContribution,
      contributionPeriods
    } = components;
    let total = principal * Math.pow(1 + annualRate, t);
    let totalContributions = principal;
    for (let i = 1; i <= t * contributionPeriods; i++) {
      total +=
        additionalContribution *
        Math.pow(1 + annualRate, t - i / contributionPeriods);
      totalContributions += additionalContribution;
    }
    return { total, totalContributions };
  };

  /**
   * Function to generate chart data based on calculation components.
   * @param components - The input components for generating chart data.
   */
  const generateChartData = (components: CompoundInterestComponents) => {
    const data = [];
    const startingAge = savingsAgeRange[0];
    for (let year = 0; year <= components.t; year++) {
      const { total, totalContributions } = calculateTotal({
        ...components,
        t: year
      });
      data.push({
        age: startingAge + year,
        saved: totalContributions.toFixed(2),
        total: total.toFixed(2),
        earned: (total - totalContributions).toFixed(2)
      });
    }
    setChartData(data);
  };

  /**
   * Function to perform calculation and update estimated savings.
   * @param components - The input components for calculation.
   */
  const handleCalculate = useCallback(
    (components: CompoundInterestComponents) => {
      const { total } = calculateTotal(components);
      setEstimatedSavings(parseFloat(total.toFixed(2)));
    },
    []
  );

  /**
   * Cleanup debounced functions on component unmount to prevent memory leaks.
   */
  useEffect(() => {
    return () => {
      debouncedTrackFormChanges.cancel();
    };
  }, [debouncedTrackFormChanges]);

  return (
    <div className="block-container">
      <div className="layout layout-padding flex-col items-center justify-center gap-6 bg-warm-neutral-100 !py-10 tablet:bg-white">
        {(moduleHeader || moduleSubheader) && (
          <div className="flex flex-col gap-3 px-4 tablet:mb-2 tablet:items-center">
            <h1 className="typography-display-3 font-glRepro font-normal tablet:text-center">
              {moduleHeader}
            </h1>
            <p className="typography-body-1 font-light tablet:w-[75%] tablet:text-center">
              {moduleSubheader}
            </p>
          </div>
        )}
        <div className="flex w-full flex-col rounded-card-shape bg-white p-6 tablet:w-[87.5%] tablet:gap-10 tablet:bg-warm-neutral-100 desktop:w-full desktop:flex-row desktop:gap-6">
          <SavingsForm
            stateHandlers={stateHandlers}
            formData={formData}
            {...fields}
          />
          <SavingsChart
            chartData={chartData}
            estimatedSavings={estimatedSavings}
            savingsAgeRange={savingsAgeRange}
            {...fields}
          />
          <div className="hidden tablet:block desktop:hidden">
            {fields.disclaimer && (
              <RichText
                data={fields.disclaimer}
                overrideOptions={RICH_TEXT_OVERRIDE}
              />
            )}
          </div>
        </div>
        {fields.ctaText && (
          <button
            className={`group/button typography-button-md mx-auto flex w-full max-w-[504px] items-center justify-center gap-2 rounded-button-shape p-4 font-medium tablet:hidden`}
            {...((fields.ctaColor || fields.ctaTextColor) && {
              style: {
                backgroundColor: fields.ctaColor,
                color: fields.ctaTextColor
              }
            })}
          >
            <a
              type="button"
              className="flex w-full items-center justify-center"
              href={
                fields.registrationButton ? webregUrl : fields.ctaDestination
              }
            >
              {fields.ctaText}
              <ArrowIcon className="ml-1 hidden transition-all duration-300 group-hover/button:block" />
            </a>
          </button>
        )}
        <div className="mr-auto block tablet:hidden desktop:block">
          {fields.disclaimer && (
            <RichText
              data={fields.disclaimer}
              overrideOptions={RICH_TEXT_OVERRIDE}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SavingsCalculator;
